import { useEffect, useState } from "react";
import { ValidationErrors, DateSet } from "../types";
import dayjs from "dayjs";
import {
  emailRegex,
  alphanumericSymbolRegex,
  numericRegex,
} from "../utils/regex";

type StringValidationRules = {
  ableToConvertNumber?: true;
  email?: true;
  isAlphanumericSymbol?: boolean;
  isNumeric?: boolean;
  notIncludes?: string[];
};
type CustomValidationRules = {
  checkDateSetValidity?: boolean;
  isBeforeNow?: boolean;
  fileRequired?: boolean;
};
type NumberValidationRules = {
  maxValue?: number;
  minValue?: number;
};
type CommonValidationRules = {
  inputRequired?: boolean;
  selectRequired?: boolean;
  maxLength?: number;
  minLength?: number;
  specificLength?: number;
};
type ValidationRule<T> = T extends number
  ? NumberValidationRules & CommonValidationRules
  : T extends string
    ? StringValidationRules & CommonValidationRules
    : CustomValidationRules;
type ValidationSet<FormTypes> = {
  [Key in keyof FormTypes]: {
    value: FormTypes[Key];
    rules: ValidationRule<FormTypes[Key]>;
  };
};
type ValidationSetProp<FormTypes> = ValidationSet<FormTypes>[keyof FormTypes];
type ValidationErrorField = {
  isValid: boolean;
  message: string | undefined;
};

const checkType = (value: any, is?: boolean) => {
  const isString = () => checkType(value, is || typeof value === "string");
  const isNumber = () => checkType(value, is || typeof value === "number");
  const isBoolean = () => checkType(value, is || typeof value === "boolean");
  const isUndefined = () =>
    checkType(value, is || typeof value === "undefined");

  return {
    isString,
    isNumber,
    isBoolean,
    isUndefined,
    is,
  };
};

export const useValidation = <FormTypes>(
  validationSet: ValidationSet<FormTypes>,
  deps: any[],
  alreadyValueSet?: boolean,
) => {
  const validationSetKeys = Object.keys(validationSet) as Array<
    keyof ValidationSet<FormTypes>
  >;

  const validate = <ValidationFormTypes>(
    formTouched: boolean,
    validationSetProp: ValidationSetProp<ValidationFormTypes>,
  ): {
    isValid: boolean;
    message: string[];
  } => {
    const formValue = validationSetProp.value as typeof validationSetProp.value;
    const validateCommon = () => {
      if (
        formTouched &&
        (typeof formValue === "string" ||
          typeof formValue === "number" ||
          typeof formValue === "undefined")
      ) {
        const rules = validationSetProp.rules as ValidationRule<string>;
        return [
          validateMaxLength(rules.maxLength, formValue),
          validateMinLength(rules.minLength, formValue),
          validateSpecificLength(rules.specificLength, formValue),
          validateInputRequired(rules.inputRequired, formValue),
          validateSelectRequired(rules.selectRequired, formValue),
          validateNotIncludes(rules.notIncludes, formValue),
        ];
      }
      return [];
    };

    const validateString = () => {
      if (formTouched && typeof formValue === "string") {
        const rules = validationSetProp.rules as ValidationRule<string>;
        return [
          validateAbleToConvertNumber(rules.ableToConvertNumber, formValue),
          validateEmailFormat(rules.email, formValue),
          validateIsAlphanumericSymbol(rules.isAlphanumericSymbol, formValue),
          validateIsNumeric(rules.isNumeric, formValue),
        ];
      }
      return [{ isValid: true, message: undefined }];
    };

    const validateNumber = () => {
      if (formTouched && typeof formValue === "number") {
        const rules = validationSetProp.rules as ValidationRule<number>;
        return [
          validateMaxValue(rules.maxValue, formValue),
          validateMinValue(rules.minValue, formValue),
        ];
      }
      return [];
    };

    const validateCustom = () => {
      const customValidateResults = [];
      if (
        formTouched &&
        !checkType(formValue).isString().isNumber().isUndefined().isBoolean().is
      ) {
        if ("checkDateSetValidity" in validationSetProp.rules) {
          // @ts-ignore
          const { year, month, date } = formValue as DateSet;
          customValidateResults.push(validateDateValidity(year, month, date));
        }
        if ("isBeforeNow" in validationSetProp.rules) {
          // @ts-ignore
          const { year, month, date } = formValue as DateSet;
          customValidateResults.push(validateDateBefore(year, month, date));
        }
      }

      if (
        formTouched &&
        !checkType(formValue).isString().isNumber().isBoolean().is &&
        "fileRequired" in validationSetProp.rules
      ) {
        const file = formValue as unknown;
        customValidateResults.push(validateFile(file as File));
      }

      return customValidateResults;
    };

    const validateResults = [
      ...validateCommon(),
      ...validateString(),
      ...validateNumber(),
      ...validateCustom(),
    ];

    return {
      isValid: validateResults.every(
        (validateResult) => validateResult.isValid,
      ),
      message: validateResults
        .map((validateResult) => validateResult.message)
        .filter((message) => message !== undefined) as NonNullable<string>[],
    };
  };

  const validateMaxLength = (
    maxLength: number | undefined,
    value: string | number | undefined,
  ): ValidationErrorField => {
    if (
      maxLength !== undefined &&
      value !== undefined &&
      value.toString().length > maxLength
    )
      return {
        isValid: false,
        message: `${maxLength}文字以下で入力してください`,
      };

    return { isValid: true, message: undefined };
  };

  const validateMinLength = (
    minLength: number | undefined,
    value: string | number | undefined,
  ): ValidationErrorField => {
    if (
      minLength !== undefined &&
      value !== undefined &&
      value.toString().length < minLength
    )
      return {
        isValid: false,
        message: `${minLength}文字以上で入力してください`,
      };

    return { isValid: true, message: undefined };
  };

  const validateInputRequired = (
    inputRequired: boolean | undefined,
    value: string | number | File | undefined,
  ): ValidationErrorField => {
    if (inputRequired && (value === undefined || value.toString() === ""))
      return {
        isValid: false,
        message: "入力してください",
      };

    return { isValid: true, message: undefined };
  };

  const validateSelectRequired = (
    selectRequired: boolean | undefined,
    value: string | number | undefined,
  ): ValidationErrorField => {
    if (selectRequired && (value === undefined || value === ""))
      return {
        isValid: false,
        message: "選択してください",
      };

    return { isValid: true, message: undefined };
  };

  const validateMaxValue = (
    maxValue: number | undefined,
    value: number | undefined,
  ): ValidationErrorField => {
    if (maxValue !== undefined && value !== undefined && value > maxValue)
      return {
        isValid: false,
        message: `${maxValue}以下で入力してください`,
      };

    return { isValid: true, message: undefined };
  };

  const validateMinValue = (
    minValue: number | undefined,
    value: number | undefined,
  ): ValidationErrorField => {
    if (minValue !== undefined && value !== undefined && value < minValue)
      return {
        isValid: false,
        message: `${minValue}以上で入力してください`,
      };

    return { isValid: true, message: undefined };
  };

  const validateAbleToConvertNumber = (
    ableToConvertNumber: true | undefined,
    value: string | undefined,
  ): ValidationErrorField => {
    if (
      ableToConvertNumber &&
      value !== undefined &&
      isNaN(parseInt(value, 10))
    ) {
      return {
        isValid: false,
        message: "数値のみを入力してください",
      };
    }

    return {
      isValid: true,
      message: undefined,
    };
  };

  const validateDateValidity = (
    year: number | undefined,
    month: number | undefined,
    date: number | undefined,
  ): ValidationErrorField => {
    const isValidDate =
      dayjs(`${year}-${month}-${date}`).format("YYYYMD") ===
      `${year}${month}${date}`;
    if (!isValidDate)
      return {
        isValid: false,
        message: "正しい日付を入力してください",
      };

    return {
      isValid: true,
      message: undefined,
    };
  };

  const validateFile = (file: File | undefined): ValidationErrorField => {
    if (!file?.name) {
      return {
        isValid: false,
        message: "ファイルが指定されていません",
      };
    }

    return {
      isValid: true,
      message: undefined,
    };
  };

  const validateDateBefore = (
    year: number | undefined,
    month: number | undefined,
    date: number | undefined,
  ): ValidationErrorField => {
    if (!dayjs(`${year}-${month}-${date}`).isBefore(dayjs(), "date"))
      return {
        isValid: false,
        message: "現在より過去の日付を入力してください",
      };

    return {
      isValid: true,
      message: undefined,
    };
  };

  const validateEmailFormat = (
    isEmail: true | undefined,
    value: string | undefined,
  ): ValidationErrorField => {
    if (isEmail && value !== undefined && !emailRegex.test(value)) {
      return {
        isValid: false,
        message: "正しいメールアドレスを入力してください",
      };
    }

    return {
      isValid: true,
      message: undefined,
    };
  };

  const validateSpecificLength = (
    specificLength: number | undefined,
    value: string | number | undefined,
  ) => {
    if (
      specificLength !== undefined &&
      value !== undefined &&
      value.toString().length !== specificLength
    )
      return {
        isValid: false,
        message: `${specificLength}文字で入力してください`,
      };

    return { isValid: true, message: undefined };
  };

  const validateIsAlphanumericSymbol = (
    isAlphanumericSymbol: boolean | undefined,
    value: string | number | undefined,
  ) => {
    if (
      isAlphanumericSymbol &&
      value !== undefined &&
      !alphanumericSymbolRegex.test(value.toString())
    )
      return {
        isValid: false,
        message: `半角英数記号のみを入力してください`,
      };

    return { isValid: true, message: undefined };
  };

  const validateIsNumeric = (
    isNumericSymbol: boolean | undefined,
    value: string | number | undefined,
  ) => {
    if (
      isNumericSymbol &&
      value !== undefined &&
      !numericRegex.test(value.toString())
    )
      return {
        isValid: false,
        message: `半角英数記号のみを入力してください`,
      };

    return { isValid: true, message: undefined };
  };

  const validateNotIncludes = (
    notIncludesValues: string[] | undefined,
    value: string | number | undefined,
  ) => {
    if (
      notIncludesValues &&
      value !== undefined &&
      notIncludesValues.includes(value.toString())
    )
      return {
        isValid: false,
        // TODO: ここだけ氏名の重複に限ったエラーメッセージになっているので
        // 他の箇所で使うことがあればエラーメッセージをhooksの引数で渡せるようにしたい
        message: `登録済みの氏名と重複しています`,
      };

    return { isValid: true, message: undefined };
  };

  const [touched, setTouched] = useState<{ [Key in keyof FormTypes]: boolean }>(
    validationSetKeys.reduce(
      (acc, key) => ({ ...acc, [key]: !!alreadyValueSet }),
      {} as { [Key in keyof FormTypes]: boolean },
    ),
  );
  const [errors, setErrors] = useState<ValidationErrors<FormTypes>>(
    validationSetKeys.reduce(
      (acc, key) => ({
        ...acc,
        [key]: validate<FormTypes>(touched[key], validationSet[key]),
      }),
      {} as ValidationErrors<FormTypes>,
    ),
  );

  const initializeState = () => {
    setTouched(
      validationSetKeys.reduce(
        (acc, key) => ({ ...acc, [key]: false }),
        {} as { [Key in keyof FormTypes]: boolean },
      ),
    );
    setErrors(
      validationSetKeys.reduce(
        (acc, key) => ({
          ...acc,
          [key]: validate<FormTypes>(touched[key], validationSet[key]),
        }),
        {} as ValidationErrors<FormTypes>,
      ),
    );
  };

  const validateAll = (ignoreTouched?: true) =>
    validationSetKeys.reduce(
      (acc, key) => ({
        ...acc,
        [key]: validate<FormTypes>(
          ignoreTouched ? true : touched[key],
          validationSet[key],
        ),
      }),
      {} as ValidationErrors<FormTypes>,
    );

  useEffect(() => {
    setErrors(validateAll());

    // validationSetをdepsに含めると無限に再renderされるため
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, touched]);

  return {
    errors,
    isFormValid: (
      Object.values(validateAll(true)) as Array<ValidationErrorField>
    ).every((error) => error.isValid),
    setTouched: (key: keyof FormTypes) =>
      setTouched((state) => ({ ...state, [key]: true })),
    setTouchedWrapper:
      <T>(
        callBack: T extends (...args: any) => any
          ? (...args: Parameters<T>) => void
          : unknown,
      ) =>
      (
        setTouchedKey: keyof FormTypes,
        ...parameters: T extends (...args: any) => void ? Parameters<T> : []
      ) => {
        setTouched((state) => ({ ...state, [setTouchedKey]: true }));
        if (typeof callBack === "function") {
          callBack(...parameters);
        }
      },
    resetState: initializeState,
  };
};
